import React, { useState, useEffect, useMemo } from 'react';
import { View, ScrollView, RefreshControl, StyleSheet } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { DesktopMainParamList } from '@/navigation/residents/desktop/DesktopNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useAppSelector, useAppDispatch } from '@/store';
import { selectLeaseIds, selectConnectionLeaseIds, linkedAcctsConnections, linkedAcctsConnectionsLoading } from '@/store/selectors';
import { useGetLeaseQuery, useCancelAutoPayMutation, leasesApi } from '@/store/api';
import { usePaymentBalance } from '@/features/payments/api/paymentHooks';
import { usePaymentMethodsList } from '@/features/payments/hooks/usePaymentMethodsList';
import { useLeaseHistory } from '@/features/payments/hooks/useLeaseHistory';
import { fetchLinkedAccounts } from '@/store/slices/residents/homeSlice';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { PageHeader } from '@/components/residents/redesign';
import {
  PaymentBalanceDueCard,
  PaymentMethodsSection,
  RecurringChargesCard,
  TransactionHistorySection,
  AutopaySection,
} from '@/components/residents/redesign/payments';
import type { RecurringChargeItem } from '@/components/residents/redesign/payments';
import { selectLease, hasAutoPay, buildAutopayLease } from '@/features/payments/utils/leaseAutoPay';
import { ConfirmationModal } from '@/components/residents/marketplace/ConfirmationModal';
import { useToast } from '@/contexts/admin/ToastContext';
import { DesktopPaymentsViewSkeleton } from './DesktopPaymentsViewSkeleton';
import { ChargeBreakdown } from '@/features/payments/components/shared/ChargeBreakdown';

const HP = REDESIGN_SPACING.horizontalPadding;
const GRID_GAP = 20;

const useLeaseDisplayData = (leaseId: number) => {
  const { balanceDue, balanceItems, accountCredit } = usePaymentBalance(leaseId);

  const leaseQuery = useGetLeaseQuery(
    { leaseId, includeTransactions: false, includePayments: true },
    { skip: !leaseId },
  );

  const { isFetching, isError, isSuccess, data: rawData, currentData, refetch } = leaseQuery;

  // A refetch of the same lease keeps `currentData`; only the first load or a
  // lease switch clears it. The page must survive a refetch, or the autopay
  // card's pending state would be swapped for the skeleton mid-removal.
  const isLoading = isFetching && !currentData;

  const data = useMemo(() => {
    if (isLoading || isError || !isSuccess || !rawData) return null;

    const lease = selectLease(rawData);
    const property = lease?.property || {};
    const scheduledCharges = lease?.scheduledCharges || [];
    const users = lease?.users || [];
    const primaryUser = users[0] || {};

    const autopayEnabled = hasAutoPay(lease);
    const propertyName = property?.marketingName || '';
    const autopayLease = buildAutopayLease({ leaseId, lease, primaryUser });

    return {
      propertyName,
      unitNumber: lease?.unitNumberSpace || lease?.unitNumber || '',
      balanceDue,
      balanceItems,
      accountCredit,
      autopayEnabled,
      autopayLease,
      recurringCharges: scheduledCharges.map((c: any): RecurringChargeItem => ({
        name: c.chargeCode || c.description || 'Charge',
        amount: c.amount ? Number(c.amount) : null,
        frequency: 'Monthly',
      })),
    };
  }, [leaseId, isLoading, isError, isSuccess, rawData, balanceDue, balanceItems, accountCredit]);

  return { data, isLoading, refetch };
};

export const DesktopPaymentsView: React.FC = () => {
  const { colors } = useThemeColors();
  const navigation = useNavigation<StackNavigationProp<DesktopMainParamList>>();
  const dispatch = useAppDispatch();
  const { isTablet } = useResponsive();
  const user = useAppSelector((s) => (s as any).auth?.user);
  const userId = user?.id;

  const ownLeaseIds = useAppSelector(selectLeaseIds);
  const connectionLeaseIds = useAppSelector(selectConnectionLeaseIds);
  const linkedAccounts = useAppSelector(linkedAcctsConnections);
  const isHomeLoading = useAppSelector(linkedAcctsConnectionsLoading);
  const [refreshing, setRefreshing] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState<{ leaseId: number; autopayId: number } | null>(null);
  // Outlives the mutation on purpose: the card reads "Removing autopay…" until
  // the refetched lease no longer carries the autopay, so it never flickers
  // back to "active" between the delete and the refetch.
  const [removingLeaseId, setRemovingLeaseId] = useState<number | null>(null);
  const [cancelAutoPay] = useCancelAutoPayMutation();
  const { showSuccess, showError } = useToast();

  const primaryLeaseId = ownLeaseIds[0] || connectionLeaseIds[0];

  useEffect(() => {
    if (!linkedAccounts || linkedAccounts.length === 0) {
      dispatch(fetchLinkedAccounts());
    }
  }, [dispatch]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await dispatch(fetchLinkedAccounts()).unwrap();
    } catch {
      // silently fail
    } finally {
      setRefreshing(false);
    }
  };

  const { data: leaseData, isLoading: leaseLoading, refetch: refetchLease } = useLeaseDisplayData(primaryLeaseId);
  const { methods: paymentMethods } = usePaymentMethodsList();

  const { rows: transactions } = useLeaseHistory(primaryLeaseId, userId);

  const subtitle = leaseData
    ? (leaseData.unitNumber
      ? `${leaseData.propertyName} · Unit ${leaseData.unitNumber}`
      : leaseData.propertyName)
    : '';

  const handleMakePayment = () => {
    if (primaryLeaseId) {
      navigation.navigate('PaymentSummaryModal', {
        leaseId: primaryLeaseId,
        residentId: user?.residentId,
      });
    }
  };

  const handleManagePaymentMethods = () => {
    navigation.navigate('ManageMyWallet' as any);
  };

  const handleAddPaymentMethod = () => {
    navigation.navigate('AddOrChangePaymentMethod' as any);
  };

  const handleViewAllTransactions = () => {
    if (primaryLeaseId) {
      navigation.navigate('TransactionHistory' as any, { leaseId: primaryLeaseId });
    }
  };

  const handleAutopaySetup = (id: number) => {
    navigation.navigate('AutoPayWizard', { leaseId: id });
  };

  const handleAutopayEdit = (id: number) => {
    navigation.navigate('AutoPayWizard', { leaseId: id });
  };

  const handleAutopayRemove = (_leaseId: number, _autopayId: number) => {
    setRemoveConfirm({ leaseId: _leaseId, autopayId: _autopayId });
  };

  const handleRemoveConfirmed = async () => {
    if (!removeConfirm?.autopayId) return;
    const { leaseId: targetLeaseId, autopayId } = removeConfirm;
    setRemoveConfirm(null);
    setRemovingLeaseId(targetLeaseId);
    try {
      await cancelAutoPay(autopayId).unwrap();
      // Refresh every variation of the lease query while each keeps its data
      // on screen (a resetApiState blanked them all and dropped this page into
      // its skeleton), then wait for this page's own copy so the pending state
      // ends on a lease that no longer carries the autopay.
      dispatch(leasesApi.util.invalidateTags([{ type: 'Lease', id: targetLeaseId }]));
      await refetchLease();
      showSuccess('Autopay removed successfully');
    } catch (err: any) {
      showError(err?.data?.message || err?.data?.error?.message || 'Failed to remove autopay');
    } finally {
      setRemovingLeaseId(null);
    }
  };

  const stacked = isTablet;
  const isDataReady = !isHomeLoading && !leaseLoading && leaseData;

  return (
  <>
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={handleRefresh}
          tintColor={colors.text.primary}
        />
      }
    >
      <PageHeader title="Payments" subtitle={subtitle} />

      {!isDataReady ? (
        <DesktopPaymentsViewSkeleton stacked={stacked} />
      ) : (
        <View style={stacked ? styles.columnsStacked : styles.columnsRow}>
          {/* Left Column: Balance, Payment Methods, Autopay, Recurring */}
          <View style={[stacked ? styles.cellFull : styles.cellWide, styles.column]}>
            <PaymentBalanceDueCard
              balanceDue={leaseData.balanceDue}
              accountCredit={leaseData.accountCredit}
              autopayEnabled={leaseData.autopayEnabled}
              recurringChargesCount={leaseData.recurringCharges.length}
              onMakePayment={handleMakePayment}
              size="large"
            />
            {((leaseData.balanceItems && leaseData.balanceItems.length > 0) || leaseData.accountCredit > 0) && (
              <ChargeBreakdown
                balanceItems={leaseData.balanceItems || []}
                balanceDue={leaseData.balanceDue}
                accountCredit={leaseData.accountCredit}
                variant="desktop"
              />
            )}
            <PaymentMethodsSection
              methods={paymentMethods}
              onManage={handleManagePaymentMethods}
              onAddNew={handleAddPaymentMethod}
              gridLayout
              showEmptyState
            />
            <AutopaySection
              leases={[leaseData.autopayLease]}
              onSetup={handleAutopaySetup}
              onEdit={handleAutopayEdit}
              onRemove={handleAutopayRemove}
              removingLeaseId={removingLeaseId}
            />
            <RecurringChargesCard charges={leaseData.recurringCharges} />
          </View>

          {/* Right Column: Transaction History */}
          <View style={[stacked ? styles.cellFull : styles.cellNarrow, styles.column]}>
            <TransactionHistorySection
              transactions={transactions}
              maxItems={15}
              onViewAll={handleViewAllTransactions}
            />
          </View>
        </View>
      )}
    </ScrollView>

    <ConfirmationModal
      visible={removeConfirm !== null}
      message="Are you sure you want to remove autopay? You will need to set it up again if you change your mind."
      onCancel={() => setRemoveConfirm(null)}
      onProceed={handleRemoveConfirmed}
      cancelText="No, cancel"
      proceedText="Yes, remove"
    />
  </>
  );
};

export default DesktopPaymentsView;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  contentContainer: {
    padding: HP,
    paddingBottom: 60,
    gap: GRID_GAP,
    maxWidth: 1100,
    width: '100%',
    alignSelf: 'center',
  },
  columnsRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: GRID_GAP,
  },
  columnsStacked: {
    gap: GRID_GAP,
  },
  column: {
    gap: GRID_GAP,
  },
  cellWide: {
    flex: 3,
    minWidth: 0,
  },
  cellNarrow: {
    flex: 2,
    minWidth: 0,
  },
  cellFull: {
    flex: 1,
  },
});
